import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function PathwaySection() {
  const { t, language } = useLanguage();

  const stages = [
    {
      level: 'K-5',
      title: t.pathway.k5Title,
      desc: t.pathway.k5Desc,
      color: '#66CA9D',
      years: '5-10 years',
    },
    {
      level: '6-8',
      title: t.pathway.middleTitle,
      desc: t.pathway.middleDesc,
      color: '#F8CD46',
      years: '11-13 years',
    },
    {
      level: '9-12',
      title: t.pathway.highTitle,
      desc: t.pathway.highDesc,
      color: '#2D78F6',
      years: '14-18 years',
    },
  ];

  return (
    <section
      data-testid="pathway-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-[#2D78F6] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.pathway.overline}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#14274B] leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.pathway.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg text-[#475569] leading-relaxed mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.pathway.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-[#475569] leading-relaxed"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'Le programme OFA France accompagne votre enfant sur l\'ensemble de sa scolarité, de la Grande Section à la Terminale. Chaque niveau est conçu pour développer progressivement les compétences académiques en anglais : fondations à l\'élémentaire, approfondissement au collège, et préparation universitaire au lycée. Les deux parcours — OFA Fluent Track et OFA Bridge Track — mènent au même objectif : le U.S. High School Diploma.'
              : 'The OFA France program supports your child throughout their entire education, from Kindergarten to 12th grade. Each level is designed to progressively develop academic English skills: foundations in elementary, deepening in middle school, and university preparation in high school. Both tracks — OFA Fluent Track and OFA Bridge Track — lead to the same goal: the U.S. High School Diploma.'}
          </motion.p>
        </div>

        {/* Pathway Timeline */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-[#E7EAEF] -translate-y-1/2" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stages.map((stage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * index }}
                data-testid={`pathway-stage-${index}`}
                className="relative bg-white"
              >
                {/* Stage Number */}
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6 relative z-10"
                  style={{ backgroundColor: stage.color }}
                >
                  <span
                    className="text-white font-bold text-lg"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {stage.level}
                  </span>
                </div>

                {/* Content */}
                <div className="text-center px-4">
                  <h3
                    className="text-xl md:text-2xl font-semibold text-[#14274B] mb-3"
                    style={{ fontFamily: 'Gabarito, sans-serif' }}
                  >
                    {stage.title}
                  </h3>

                  <p
                    className="text-[#475569] leading-relaxed mb-4"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {stage.desc}
                  </p>

                  <p
                    className="text-sm text-[#2D78F6] font-medium"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {stage.years}
                  </p>
                </div>

                {/* Arrow - Desktop */}
                {index < stages.length - 1 && (
                  <div className="hidden md:flex absolute top-8 -right-4 z-20">
                    <ArrowRight className="w-8 h-8 text-[#E7EAEF]" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mt-16"
        >
          <Link to="/programs">
            <Button
              data-testid="pathway-cta"
              className="bg-[#14274B] text-white hover:bg-[#2D78F6] px-8 py-6 text-base font-medium transition-all duration-300"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.common.viewPrograms}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
